import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { config, SCREENCAST_LOG_SHEET } from './config';
import { TokenEncryptionService } from './Services/token-encryption.service';
import { clearStoragePreservingAttribution } from './utils/attribution.util';

@Injectable()
export class HeaderInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private tokenEncryptionService: TokenEncryptionService
  ) { }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!navigator.onLine) {
      this.showSnackBar('Please check your internet connection');
      return throwError(() => new Error('No internet connection'));
    }

    const token = this.tokenEncryptionService.getToken();
    const requestHeader = req.headers.get('authorization');
    const skipHeader = req.headers.get('skip');

    const shouldAttachToken =
      token &&
      !requestHeader &&
      !skipHeader &&
      req.url !== config.feedBackSheet &&
      req.url !== SCREENCAST_LOG_SHEET.url &&
      !req.url.includes('https://s3.ap-south-1.amazonaws.com/');

    const modifiedRequest = shouldAttachToken
      ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${token}`) })
      : req;

    return next.handle(modifiedRequest).pipe(
      catchError((error) => this.handleError(error, modifiedRequest.url))
    );
  }

  private handleError(error: any, requestUrl: string): Observable<never> {
    switch (error.status) {
      case 401:
        // WooCommerce REST uses separate Basic credentials — do not clear JWT session.
        if (!this.isWooCommerceRestRequest(requestUrl)) {
          this.handleUnauthorizedError();
        }
        break;
      case 404:
        if (error.error?.code === 'rest_user_invalid_id') {
          this.handleInvalidUserIdError(error.error.message);
        }
        break;
      default:
        // Handle other error statuses if needed
        break;
    }
    return throwError(error);
  }

  /** Billing page calls wc/v3 with payment_username/password, not the user JWT. */
  private isWooCommerceRestRequest(url: string): boolean {
    return url.includes('/wc/v3') || url.includes('wc/v3/');
  }

  private handleUnauthorizedError(): void {
    this.tokenEncryptionService.clearAll();
    clearStoragePreservingAttribution(localStorage);
    this.router.navigate(['/login']);
  }

  private handleInvalidUserIdError(message: string): void {
    this.router.navigate(['/employees']);
    this.showSnackBar(message);
  }

  private showSnackBar(message: string): void {
    this.snackBar.open(message, 'Undo', { duration: 3000 });
  }
}
